import Layout from "layout";
import React, { useEffect, useState } from "react";
import {
  useGetAdminList,
  useDeleteAdmin,
  useToggleAdminStatus,
  useResetAdminPassword,
} from "utils/api/admin.api";
import { type AdminListResponse } from "utils/types/user.type";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { IconInfoCircle } from "@tabler/icons-react";
import CreateAdmin from "components/createAdmin";

export default function AdminAccess() {
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState(
    searchParams.get("search") ?? ""
  );
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [isCreateAdminOpen, setIsCreateAdminOpen] = useState(false);

  const { data, isLoading } = useGetAdminList({
    page,
    limit,
    search_query: searchValue || undefined,
  });
  return (
    <Layout>
      <div className="dashboard-main">
        <div className="main-wrapper max-w-5xl p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <ul className="flex flex-wrap items-center gap-3">
              <li>
                <p className="text-black-900 text-xl"> Admin Management</p>
              </li>
            </ul>
            <button
              onClick={() => setIsCreateAdminOpen(true)}
              className="hover:bg-cyan-800 bg-cyan-300 border border-cyan-300 rounded-lg text-black-700 text-base font-bold transition px-6 py-2.5"
            >
              Register New Admin
            </button>
          </div>
          <form action="#" className="flex items-center gap-6 ">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="obasaolorunfemi@gmail.com"
              className="w-full text-black-700 text-sm rounded-lg border border-black-800 focus:outline-0 py-3.5 px-5"
            />
            {/* <button className="hover:bg-cyan-800 bg-cyan-300 border border-cyan-300 rounded-lg text-black-700 text-base font-bold transition px-8 lg:px-16 py-3">
              Search
            </button> */}
          </form>

          <div className="table-wrapper max-h-[48rem] overflow-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="rounded-l-lg border border-black-800/30 border-r-0 py-5 px-5 text-left">
                    Date
                  </th>
                  <th className="py-5 px-5 border-y border-black-800/30 text-left">
                    Name
                  </th>
                  <th className="py-5 px-5 border-y border-black-800/30 text-left">
                    Email
                  </th>
                  <th className="py-5 px-5 border-y border-black-800/30 text-left">
                    Last Active
                  </th>
                  <th className="py-5 px-5 border-y border-black-800/30 text-left">
                    Password Reset
                  </th>
                  <th className="py-5 px-5 border-y border-black-800/30 text-left">
                    Action
                  </th>
                  <th className="rounded-r-lg border border-black-800/30 border-l-0 py-5 px-16 text-left">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {data?.records?.map((item) => (
                  <TableRow key={item?.id} admin={item} />
                ))}
                {!isLoading && data?.records?.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      No Results Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {data && data.total > limit && (
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-cyan-800 text-white rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <span>
                  Page {page} of {Math.ceil(data.total / limit)}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= Math.ceil(data.total / limit)}
                  className="px-4 py-2 bg-cyan-800 text-white rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <CreateAdmin isOpen={isCreateAdminOpen} setIsOpen={setIsCreateAdminOpen} />
    </Layout>
  );
}

function TableRow({ admin }: { admin: AdminListResponse["records"][0] }) {
  const [isActive, setIsActive] = useState(Boolean(admin?.is_active));
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  const { mutate: toggleStatus, isLoading: toggleLoading, isSuccess: toggleSuccess } =
    useToggleAdminStatus();
  const { mutate: deleteAdmin, isLoading: deleteLoading, isSuccess: deleteSuccess } =
    useDeleteAdmin();
  const { mutate: resetPassword, isLoading: resetLoading, isSuccess: resetSuccess } =
    useResetAdminPassword();

  // Sync state with admin prop
  useEffect(() => {
    setIsActive(Boolean(admin?.is_active));
  }, [admin?.is_active]);

  useEffect(() => {
    if (toggleSuccess) {
      toast.success("Admin status updated successfully!");
    }
  }, [toggleSuccess]);

  useEffect(() => {
    if (deleteSuccess) {
      toast.success("Admin deleted successfully!");
      setShowDeleteConfirm(false);
    }
  }, [deleteSuccess]);

  useEffect(() => {
    if (resetSuccess) {
      toast.success("Password reset successfully!");
      setShowResetPassword(false);
      setNewPassword("");
    }
  }, [resetSuccess]);

  const handleToggleStatus = () => {
    toggleStatus(admin.id);
  };

  const handleDelete = () => {
    deleteAdmin(admin.id);
  };

  const handleResetPassword = () => {
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    resetPassword({ id: admin.id, password: newPassword });
  };

  return (
    <>
      {/* className={`w-full rounded-lg px-2 ${
            isEdit ? "bg-cyan-300" : "bg-black-300"
          }`} */}
      <tr className="bg-black-300">
        <td className="py-3 px-5 rounded-l-lg">
          <p className="text-black-900 text-xs">
            {new Date(admin?.created_at).toLocaleDateString("us", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </td>
        <td className="py-3 px-2">
          <p className="text-black-900 text-xs">{admin?.name}</p>
        </td>
        <td className="py-3 px-2">
          <p className="text-black-900 text-xs font-medium underline">
            {admin?.email}
          </p>
        </td>
        <td className="py-3 px-2 text-center">
          <p className="text-black-900 text-xs">
            {admin?.last_active
              ? new Date(admin.last_active).toLocaleDateString("us", {
                hour: "2-digit",
                minute: "2-digit",
              })
              : "Never"}
          </p>
        </td>
        <td className="py-3 px-2 text-center">
          <div className="flex items-center justify-center  gap-2">
            <button
              onClick={() => {
                setShowResetPassword(!showResetPassword)
                setShowDeleteConfirm(false)
              }}
              className="text-xs px-3 py-1 bg-orange-600 text-white rounded hover:bg-orange-700"
            >
              Reset
            </button>

            <div className={`relative group ${admin?.password_reset_requested ? "visible" : "invisible"}`}>
              <IconInfoCircle className="w-4 h-4 text-orange-600 cursor-pointer" />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                {admin?.name} has requested to reset the password
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                  <div className="border-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            </div>

          </div>
        </td>
        <td className="py-3 px-2 text-center">
          <div className="flex gap-2 justify-center items-center">
            <button
              className="text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
              onClick={() => {
                setShowDeleteConfirm(!showDeleteConfirm)
                setShowResetPassword(false)
              }}
            >
              Delete
            </button>
          </div>
        </td>
        <td className="py-3 px-2 rounded-r-lg text-center">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={handleToggleStatus}
              disabled={toggleLoading}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-800"></div>
            <span className="ml-3 text-xs text-black-900">
              {isActive ? "Active" : "Inactive"}
            </span>
          </label>
        </td>
      </tr>
      {showResetPassword && (
        <tr className="bg-orange-50">
          <td colSpan={7} className="px-6 py-4">
            <div className="space-y-3">
              <p className="text-sm font-semibold">Reset Password for {admin.name}</p>
              <div className="flex items-center gap-2">
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                  className="flex-1 text-sm border border-gray-300 rounded px-3 py-2"
                  minLength={8}
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowResetPassword(false);
                      setNewPassword("");
                    }}
                    className="text-xs px-4 py-2 bg-gray-300 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetPassword}
                    disabled={resetLoading || newPassword.length < 8}
                    className="text-xs px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                  >
                    {resetLoading ? "Resetting..." : "Reset"}
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
      {showDeleteConfirm && (
        <tr className="bg-red-50">
          <td colSpan={7} className="px-6 py-4">
            <div className="flex items-center justify-between">
              <p className="text-sm">Are you sure you want to delete this admin?</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="text-xs px-4 py-2 bg-gray-300 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteLoading}
                  className="text-xs px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  {deleteLoading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
